import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';

import type { MutableRefObject, ReactNode } from 'react';

import { Sticky } from '@travel/ui';
import { cx } from '@travel/utils';

import ButtonNoStyle from 'components/ButtonNoStyle';

import { setSelectedNavSection } from 'store/providerInformation/actions';
import { isClient } from 'store/utils';

import useDivElementHeight from 'hooks/useDivElementHeight';

import styles from './scrollingNavigation.module.scss';

export type Section = {
  id: string;
  title: string;
  target: MutableRefObject<HTMLDivElement | null>;
};

type Props = {
  sections: Section[];
  selectedSection?: string;
  otherChildren?: ReactNode;
  positionTop?: number;
  /** optional fn to be called when a section is selected */
  onSelectSection?: (id: string) => void;
};

export type ScrollingNavigationHandle = {
  scrollToTarget: (sectionId: string, behavior?: ScrollBehavior) => void;
};

// The distance from the element sticky to the top
const topDistance = 60;

const ScrollingNavigation = forwardRef<ScrollingNavigationHandle, Props>((props, ref) => {
  const { sections, selectedSection, otherChildren, positionTop = 0, onSelectSection } = props;
  const navSections = sections.filter((section) => section);

  const [active, setActive] = useState<string>(selectedSection || navSections[0].id);
  const [itemWidth, setItemWidth] = useState<number | null>(null);
  const [itemLeft, setItemLeft] = useState<number | null>(null);
  const [contentTop, setContentTop] = useState<number | null>(null);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const itemsRef = useRef<HTMLLIElement[] | null[]>([]);
  const contentHeight = useDivElementHeight(contentRef);

  const dispatch = useDispatch();

  const handleScroll = useCallback(() => {
    navSections.forEach(({ id, target }, index) => {
      const sectionRect = target.current?.getBoundingClientRect();

      const inViewThreshold = contentHeight + positionTop + topDistance; // Can be regarded as user enters this section when scrolling the screen to where the section is 60px under the navbar.

      setContentTop(Number(contentRef.current?.offsetTop));

      if (
        sectionRect &&
        sectionRect.top <= inViewThreshold &&
        sectionRect.bottom > inViewThreshold
      ) {
        const width = itemsRef.current[index]?.getBoundingClientRect().width;
        const left = itemsRef.current[index]?.offsetLeft;

        setActive(id);
        setItemWidth(Number(width));
        setItemLeft(Number(left));

        dispatch(setSelectedNavSection(navSections[index]));
      }
    });
  }, [contentHeight, dispatch, navSections, positionTop]);

  useEffect(() => {
    isClient && window.addEventListener('scroll', handleScroll);

    return () => {
      isClient && window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const handleNavItemClick = (id: string) => () => {
    scrollToTarget(id);
    onSelectSection?.(id);
  };

  const scrollToTarget = useCallback(
    (sectionId: string, behavior: ScrollBehavior = 'smooth') => {
      const offsetTop =
        navSections.find(({ id }) => id === sectionId)?.target.current?.offsetTop ?? 0;
      isClient &&
        window.scrollTo({
          top: offsetTop - contentHeight - positionTop,
          // if you want instant scroll; think: hash navigation
          behavior,
        });
    },
    [contentHeight, navSections, positionTop],
  );

  // if parent wants to trigger a scroll directly
  // exposing this function up lets parent do so without owning offset math
  // for context: `sections` is a whole bunch of refs set in parent
  // this is the sticky bar that lets user scroll to refs in parent
  useImperativeHandle(ref, () => ({ scrollToTarget }), [scrollToTarget]);

  return (
    <Sticky
      position="top"
      floatingStateClassName={styles.floating}
      className={styles.wrapper}
      rootMargin={`-${contentHeight + positionTop}px 0px 0px 0px`}
      childrenHeight={contentHeight}
      positionTop={positionTop}
    >
      <div
        className={styles.content}
        ref={contentRef}
        data-testid="scrollingNavigationBar-container"
      >
        <ul className={styles.navItemList}>
          {navSections.map(({ id, title }, index) => (
            <li
              className={cx(styles.navItem, id === active && styles.selectedItem)}
              key={id}
              data-testid={`scrollingNavigationBar-item-${id}`}
              data-selected={id === active}
              ref={(el) => (itemsRef.current[index] = el)}
            >
              <ButtonNoStyle onClick={handleNavItemClick(id)}>{title}</ButtonNoStyle>
              {index === 0 && contentTop === 0 ? (
                <hr
                  className={styles.activeBorder}
                  style={{
                    width: itemWidth || 0,
                    left: itemLeft || 0,
                  }}
                />
              ) : null}
            </li>
          ))}
        </ul>
        {otherChildren}
      </div>
    </Sticky>
  );
});

ScrollingNavigation.displayName = 'ScrollingNavigation';

export default ScrollingNavigation;
